import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Excel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Single Upload States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [snaLastUpdated, setSnaLastUpdated] = useState(null);
  const [tmpLastUpdated, setTmpLastUpdated] = useState(null);
  
  const navigate = useNavigate();

  // Fetch last updated dates from the unified API
  const fetchLastUpdated = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`https://crd-training.in/api/v1/tmp/last-updated`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      const responseData = await response.json();
      
      if (!responseData.error && responseData.data) {
        // Find SNA and TMP entries from the data array
        const snaData = responseData.data.find(item => item.name === "SNA");
        const tmpData = responseData.data.find(item => item.name === "TMP");
        
        if (snaData && snaData.date) {
          setSnaLastUpdated(snaData.date);
        }
        
        if (tmpData && tmpData.date) {
          setTmpLastUpdated(tmpData.date);
        }
      }
    } catch (err) {
      console.error('Error fetching last updated dates:', err);
    }
  };

  // Upload Excel file to TMP endpoint (backend will process and split)
  const uploadFile = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(false);
      
      const authToken = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(`https://crd-training.in/api/v1/tmp`, {
        method: 'POST',
        headers: {
          'Authorization': authToken
        },
        body: formData
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.success) {
        setUploadSuccess(true);
        setSelectedFile(null);
        // Refresh last updated dates after successful upload
        await fetchLastUpdated();
        
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadSuccess(false);
        }, 2000);
      } else {
        setUploadError(responseData.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError('Network error: ' + err.message);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        setSelectedFile(null);
      }
    }
  };

  // Open upload modal
  const openUploadModal = async () => {
    setShowUploadModal(true);
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(null);
    await fetchLastUpdated();
  };

  // Close upload modal
  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(null);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchLastUpdated();
  }, []);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Portal Uploads</h1>
          <div className="flex gap-4 mt-2">
            {snaLastUpdated && (
              <p className="text-sm text-gray-600">
                SNA Last updated: {new Date(snaLastUpdated).toLocaleString('en-IN')}
              </p>
            )}
            {tmpLastUpdated && (
              <p className="text-sm text-gray-600">
                TMP Last updated: {new Date(tmpLastUpdated).toLocaleString('en-IN')}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          {/* Upload Document Button */}
          <button
            onClick={openUploadModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Documents
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          <p className="font-medium">Error: {error}</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-blue-600">Upload Document</h3>
              <button
                onClick={closeUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Show SNA and TMP last updated dates in modal */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {snaLastUpdated && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                  <p className="text-sm text-purple-800">
                    <span className="font-medium">SNA Last Updated:</span><br />
                    {new Date(snaLastUpdated).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
              {tmpLastUpdated && (
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">TMP Last Updated:</span><br />
                    {new Date(tmpLastUpdated).toLocaleString('en-IN')}
                  </p>
                </div>
              )}
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Excel File (.xlsx or .xls)
              </label>
              <div className="mt-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  disabled={uploading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Select an Excel file to upload your document data.
                </p>
              </div>
              {selectedFile && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Selected:</span> {selectedFile.name}
                  </p>
                  <p className="text-xs text-green-600">
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
            
            {uploadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                <p className="text-sm">{uploadError}</p>
              </div>
            )}
            
            {uploadSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">
                <p className="text-sm font-medium">Document uploaded successfully!</p>
                <p className="text-xs text-green-600 mt-1">Your file has been processed and saved.</p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <button
                onClick={closeUploadModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={uploadFile}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-t-2 border-white"></div>
                )}
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Excel;